import { Logger } from '@frontend/logger';
import { useAccountRepository } from '@frontend/repository';
import { useCallback, useEffect, useState } from 'react';

import { KioskListProps } from './kiosk-list.component';

const DEFAULT_PAGE_SIZE = 25;

interface ViewProps<T extends object> {
    data: T[] | null;
    loading: boolean;
    loadingMore: boolean;
    error: string | null;
    hasMore: boolean;
    loadMore: () => void;
}

const useKioskList = <T extends { id: string }>(props: KioskListProps<T>): ViewProps<T> => {
    const [data, setData] = useState<T[] | null>(null);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [totalCount, setTotalCount] = useState(0);
    const [pageIndex, setPageIndex] = useState(0);

    const { currentAccount } = useAccountRepository();
    const pageSize = props.pageSize ?? DEFAULT_PAGE_SIZE;

    useEffect(() => {
        setError(null);
        setPageIndex(0);
        const fetchData = async () => {
            setLoading(true);
            try {
                const response = await props.fetch({
                    account_id: currentAccount,
                    index: '0',
                    size: pageSize.toString()
                });
                setData(response.results);
                setTotalCount(response.count);
                props.onDataLoaded?.(response.results);
            } catch (err) {
                Logger.error('Failed to fetch list data', err);
                setError('Failed to load data');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [currentAccount, props.fetch, pageSize]);

    const loadMore = useCallback(() => {
        if (loadingMore || !data || data.length >= totalCount) return;
        const nextIndex = pageIndex + 1;
        setPageIndex(nextIndex);
        setLoadingMore(true);
        props
            .fetch({
                account_id: currentAccount,
                index: nextIndex.toString(),
                size: pageSize.toString()
            })
            .then((response) => {
                setData((prev) => [...(prev ?? []), ...response.results]);
                setTotalCount(response.count);
                props.onDataLoaded?.(response.results);
            })
            .catch((err) => {
                Logger.error('Failed to fetch more list data', err);
            })
            .finally(() => setLoadingMore(false));
    }, [currentAccount, data, loadingMore, pageIndex, pageSize, totalCount, props.fetch]);

    const hasMore = (data?.length ?? 0) < totalCount && totalCount > 0;

    return {
        data,
        loading,
        loadingMore,
        error,
        hasMore,
        loadMore
    };
};

export default useKioskList;
